import { CacheKey, SyncStatusCode } from '@signage-player/data-store';
import { WebsocketContentSync } from './WebsocketContentSync';
import { PollingContentSync } from './PollingContentSync';
import { CacheStoreTag } from '@signage-player/cache';
export class ContentSyncManager {
    constructor(config, logger, authInstance, api, cacheManager) {
        this.config = config;
        this.logger = logger;
        this.authInstance = authInstance;
        this.api = api;
        this.cacheManager = cacheManager;
        this.eventlistCallback = null;
        this.deviceCallback = null;
        this.timeCallback = null;
        this.statusCallback = null;
        this.lastEventlistFetchAt = null;
        this.lastDeviceFetchAt = null;
        this.isLowBandwidthMode = false;
        this.isWebSocketConnected = false;
        this.lowBandwidthModeCallback = null;
        // Jitter is applied per-tick in PollingContentSync via jitter() (±20%).
        this.pollingIntervalsWithWebsockets = {
            eventlistPollInterval: 60 * 60 * 1000, // 1 hour
            devicePollInterval: 60 * 60 * 1000, // 1 hour
            timePollInterval: 60 * 1000, // 1 minute
        };
        this.pollingIntervalsWithoutWebsockets = {
            eventlistPollInterval: 60 * 1000, // 1 minute
            devicePollInterval: 60 * 1000, // 1 minute
            timePollInterval: 60 * 1000, // 1 minute
        };
        this.pollingIntervalsLowBandwidthMode = {
            eventlistPollInterval: 60 * 60 * 1000, // 1 hour
            devicePollInterval: 15 * 60 * 1000, // 15 minutes
            timePollInterval: 15 * 60 * 1000, // 15 minutes
        };
        this.contentSyncs = new Map();
    }
    async initializeEventlistSync(newDataCallback) {
        this.eventlistCallback = eventList => {
            this.cacheManager.tag(CacheStoreTag.Persistent).set(CacheKey.EventList, eventList);
            newDataCallback(eventList);
        };
    }
    async initializeDeviceSync(newDataCallback) {
        this.deviceCallback = device => {
            // clear action from device and displays to avoid infinite loop
            const deviceDataToCache = {
                ...device,
                action: null,
                displays: device.displays.map(d => ({ ...d, action: null })),
            };
            this.cacheManager.tag(CacheStoreTag.Persistent).set(CacheKey.DeviceData, deviceDataToCache);
            newDataCallback(device);
        };
    }
    async initializeTimeSync(newDataCallback) {
        this.timeCallback = newDataCallback;
    }
    initializeStatusHandler(statusCallback) {
        this.statusCallback = statusCallback;
    }
    initializeLowBandwidthModeHandler(callback) {
        this.lowBandwidthModeCallback = callback;
    }
    startContentSyncing() {
        const pollingOptions = this.config.dynamicConfig.websocketsEnabled ? this.pollingIntervalsWithWebsockets : this.pollingIntervalsWithoutWebsockets;
        const pollingContentSync = new PollingContentSync(this.api, this.logger, {
            ...pollingOptions,
            timePollInterval: this.config.dynamicConfig.timeSyncAdapter ? pollingOptions.timePollInterval : 0,
        });
        pollingContentSync.initializeEventlistSync(eventlist => this.handleNewEventlist(eventlist));
        pollingContentSync.initializeDeviceSync(device => this.handleNewDevice(device));
        if (this.timeCallback) {
            pollingContentSync.initializeTimeSync(this.timeCallback);
        }
        pollingContentSync.initializeStatusHandler(status => this.handleStatus(status));
        this.contentSyncs.set('polling', pollingContentSync);
        if (this.config.dynamicConfig.websocketsEnabled) {
            const websocketContentSync = new WebsocketContentSync(this.config, this.logger, this.api, this.authInstance);
            websocketContentSync.initializeEventlistSync(eventlist => this.handleNewEventlist(eventlist));
            websocketContentSync.initializeDeviceSync(device => this.handleNewDevice(device));
            if (this.timeCallback) {
                websocketContentSync.initializeTimeSync(this.timeCallback);
            }
            websocketContentSync.initializeStatusHandler(status => this.handleStatus(status));
            this.contentSyncs.set('websocket', websocketContentSync);
        }
    }
    destroy() {
        this.contentSyncs.forEach((contentSync, key) => {
            this.logger.debug('[ContentSyncManager] Stopping content sync: ' + key);
            contentSync.destroy();
        });
        this.contentSyncs.clear();
    }
    handleNewEventlist(eventList) {
        this.lastEventlistFetchAt = new Date();
        if (this.eventlistCallback) {
            this.eventlistCallback(eventList);
        }
    }
    handleNewDevice(device) {
        this.lastDeviceFetchAt = new Date();
        const newLowBandwidthMode = device.low_bandwidth_mode;
        const modeChanged = newLowBandwidthMode !== this.isLowBandwidthMode;
        this.isLowBandwidthMode = newLowBandwidthMode;
        if (modeChanged) {
            if (this.lowBandwidthModeCallback) {
                this.lowBandwidthModeCallback(this.isLowBandwidthMode);
            }
            if (!this.isWebSocketConnected && this.lastEventlistFetchAt && this.lastDeviceFetchAt) {
                const pollingSync = this.contentSyncs.get('polling');
                const intervals = this.isLowBandwidthMode ? this.pollingIntervalsLowBandwidthMode : this.pollingIntervalsWithoutWebsockets;
                pollingSync.setNewIntervals({ ...intervals, timePollInterval: this.config.dynamicConfig.timeSyncAdapter ? intervals.timePollInterval : 0 }, { eventList: this.lastEventlistFetchAt, device: this.lastDeviceFetchAt });
                this.logger.debug('[ContentSyncManager] Low bandwidth mode changed, adjusted polling intervals accordingly.', {
                    lowBandwidthMode: this.isLowBandwidthMode,
                });
            }
        }
        if (this.deviceCallback) {
            this.deviceCallback(device);
        }
    }
    handleStatus(status) {
        // custom status: WS connected
        if (status.code === SyncStatusCode.WebSocketConnected) {
            this.isWebSocketConnected = true;
            const pollingSync = this.contentSyncs.get('polling');
            pollingSync.setNewIntervals({
                ...this.pollingIntervalsWithWebsockets,
                timePollInterval: this.config.dynamicConfig.timeSyncAdapter ? this.pollingIntervalsWithWebsockets.timePollInterval : 0,
            }, { eventList: this.lastEventlistFetchAt, device: this.lastDeviceFetchAt });
            this.logger.debug('[ContentSyncManager] WebSocket reconnected, adjusted polling intervals accordingly.');
            if (this.statusCallback) {
                this.statusCallback({ code: SyncStatusCode.ConnectionEstablished });
            }
            return;
        }
        // custom status: WS disconnected
        if (status.code === SyncStatusCode.WebSocketDisconnected) {
            this.isWebSocketConnected = false;
            const pollingSync = this.contentSyncs.get('polling');
            const intervals = this.isLowBandwidthMode ? this.pollingIntervalsLowBandwidthMode : this.pollingIntervalsWithoutWebsockets;
            pollingSync.setNewIntervals({ ...intervals, timePollInterval: this.config.dynamicConfig.timeSyncAdapter ? intervals.timePollInterval : 0 }, { eventList: this.lastEventlistFetchAt, device: this.lastDeviceFetchAt });
            this.logger.debug('[ContentSyncManager] WebSocket disconnected, adjusted polling intervals accordingly.', { lowBandwidthMode: this.isLowBandwidthMode });
            return;
        }
        if (this.statusCallback) {
            this.statusCallback(status);
        }
    }
    getCachedEventList() {
        return this.cacheManager.tag(CacheStoreTag.Persistent).get(CacheKey.EventList);
    }
    getCachedDeviceData() {
        return this.cacheManager.tag(CacheStoreTag.Persistent).get(CacheKey.DeviceData);
    }
}
//# sourceMappingURL=ContentSyncManager.js.map